import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadBlob } from "@/lib/download";
import AuthImage from "@/components/AuthImage";
import FeedView from "@/components/FeedView";
import HeroBanner from "@/components/HeroBanner";
import { HEROES, NAV_ICONS } from "@/lib/brandImages";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import WorldClock from "@/components/WorldClock";
import WorldNews from "@/components/WorldNews";
import CensusCard from "@/components/CensusCard";
import {
  Flame, Trophy, Users, Sailboat, Cake, Target, BookText, ShieldAlert, FileDown, Megaphone, Coins,
} from "lucide-react";
import { StatusBadge } from "@/components/island/Dues";

function Chip({ icon: Icon, label, value, tint }) {
  return (
    <Card className="gold-ring glass flex items-center gap-3 p-4" data-testid={`chip-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="font-display text-xl font-700 leading-none">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}

function GalleonNews() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/posts", { params: { scope: "galleon", type: "post" } }).then((r) => setItems(r.data.posts.filter((p) => p.is_announcement).slice(0, 4)));
  }, []);
  if (items.length === 0) return null;
  return (
    <Card className="glass border-[hsl(var(--gold))]/55 p-5" data-testid="galleon-news">
      <div className="mb-3 flex items-center gap-2"><Megaphone className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Galleon News & Events</h3></div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((p) => (
          <div key={p.id} className="flex gap-3 rounded-xl bg-[hsl(var(--gold))]/10 p-3" data-testid={`galleon-news-${p.id}`}>
            {p.image_path && <AuthImage path={p.image_path} className="h-16 w-16 shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0"><p className="line-clamp-3 text-sm font-600">{p.content}</p><p className="mt-1 text-[0.7rem] text-muted-foreground">{p.author_name} · {new Date(p.created_at).toLocaleDateString()}</p></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function MyDues() {
  const [dues, setDues] = useState([]);
  useEffect(() => { api.get("/island/dues").then((r) => setDues(r.data.dues.filter((d) => d.my_status !== "waived").slice(0, 4))).catch(() => {}); }, []);
  if (dues.length === 0) return null;
  const owing = dues.filter((d) => d.my_status !== "paid");
  return (
    <Card className={`glass p-5 ${owing.length ? "border-amber-400/60" : "gold-ring"}`} data-testid="my-dues">
      <div className="mb-3 flex items-center gap-2"><Coins className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">My Dues</h3><Link to="/island" className="ml-auto text-xs text-[hsl(var(--water))]">Island → Dues</Link></div>
      <div className="space-y-2">
        {dues.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`my-dues-${d.id}`}>
            <div className="min-w-0"><p className="truncate font-600">{d.title}</p><p className="text-[0.65rem] text-muted-foreground">{d.frigate || "Galleon"} · {(d.currency || "USD") === "USD" ? "$" : d.currency + " "}{Number(d.amount).toLocaleString()}{d.due_date ? ` · due ${d.due_date}` : ""}</p></div>
            <StatusBadge status={d.my_status} />
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [eng, setEng] = useState(null);
  const [bdays, setBdays] = useState([]);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data.stats));
    api.get("/engagement").then((r) => setEng(r.data)).catch(() => {});
    api.get("/birthdays").then((r) => setBdays(r.data.birthdays));
    api.get("/goals").then((r) => setGoals(r.data.goals)).catch(() => {});
  }, []);

  return (
    <div>
      <HeroBanner image={HEROES.galleon} icon={NAV_ICONS["/dashboard"]} eyebrow={`Alora, ${user?.name?.split(" ")[0] || ""}${user?.deck_name ? ` · "${user.deck_name}"` : ""}`} title="IOTA Galleon Mess" description={`World clock, live news, birthdays, reel and the pulse of the brotherhood${user?.frigate ? ` — sailing with ${user.frigate} Frigate` : ""}.`} testid="dashboard-hero" />

      <div className="mb-6"><WorldClock /></div>

      {bdays.some((b) => b.is_today || b.days_until <= 7) && (
        <Card className="gold-ring glass mb-6 flex flex-wrap items-center gap-3 border-pink-400/40 p-4" data-testid="birthday-reminder">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-500/15 text-pink-500"><Cake className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-700">Birthday reminder</p>
            <p className="text-xs text-muted-foreground">
              {bdays.filter((b) => b.is_today || b.days_until <= 7).map((b) => `${b.name}${b.is_today ? " (today!)" : ` in ${b.days_until}d`}`).join(" · ")}
            </p>
          </div>
        </Card>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Chip icon={Flame} label="Day Streak" value={stats?.streak ?? "—"} tint="bg-orange-500/15 text-orange-500" />
        <Chip icon={Trophy} label="Points" value={stats?.points ?? "—"} tint="bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]" />
        <Chip icon={Users} label="Brothers" value={stats?.members ?? "—"} tint="bg-[hsl(var(--water))]/15 text-[hsl(var(--water))]" />
        <Chip icon={Sailboat} label={stats?.frigate ? `${stats.frigate} Crew` : "Rank"} value={stats?.frigate ? (stats?.frigate_members ?? "—") : (eng?.tier ?? "—")} tint="bg-emerald-500/15 text-emerald-500" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <GalleonNews />
          <FeedView scope="galleon" showRegions={false} showReel />
        </div>

        <div className="space-y-6">
          <CensusCard />
          <MyDues />
          <WorldNews />
          <Card className="gold-ring glass p-6">
            <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Engagement Rank</h3></div>
            {eng && (
              <div className="mt-3">
                <p className="font-display text-2xl font-700 gold-text">{eng.tier}</p>
                <p className="text-sm text-muted-foreground">{eng.points} points</p>
                {eng.next_tier ? (
                  <div className="mt-3">
                    <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>Next: {eng.next_tier.name}</span><span>{eng.points}/{eng.next_tier.points}</span></div>
                    <Progress value={(eng.points / eng.next_tier.points) * 100} className="h-2" />
                  </div>
                ) : <Badge className="mt-3 bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]">Top rank ⚓</Badge>}
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-orange-500/10 p-2.5 text-xs text-orange-500"><Flame className="h-4 w-4" />{eng.streak}-day streak — keep it alive!</div>
              </div>
            )}
          </Card>

          <Card className="gold-ring glass p-6">
            <div className="mb-3 flex items-center gap-2"><Cake className="h-5 w-5 text-pink-500" /><h3 className="font-display text-lg font-700">Upcoming Birthdays</h3></div>
            <div className="space-y-3">
              {bdays.slice(0, 6).map((b) => (
                <div key={b.id} className="flex items-center gap-3" data-testid={`birthday-${b.id}`}>
                  <Avatar className="h-9 w-9">
                    {b.photo_path ? <AuthImage path={b.photo_path} className="h-9 w-9 rounded-full object-cover" /> : <AvatarFallback className="bg-pink-500/15 text-xs text-pink-500">{b.name[0]}</AvatarFallback>}
                  </Avatar>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-600">{b.name}</p><p className="text-xs text-muted-foreground">{b.frigate || "Galleon"} · {b.birthday}</p></div>
                  {b.is_today ? <Badge className="bg-pink-500 text-white">Today 🎉</Badge> : <span className="text-xs text-muted-foreground">in {b.days_until}d</span>}
                </div>
              ))}
              {bdays.length === 0 && <p className="text-sm text-muted-foreground">No birthdays on record.</p>}
            </div>
          </Card>

          <Card className="gold-ring glass p-6">
            <div className="mb-3 flex items-center gap-2"><Target className="h-5 w-5 text-[hsl(var(--water))]" /><h3 className="font-display text-lg font-700">Voyage Progress</h3></div>
            <div className="space-y-3">
              {goals.slice(0, 4).map((g) => (
                <div key={g.id}>
                  <div className="mb-1 flex justify-between text-sm"><span className="font-600">{g.title}</span><span className="font-mono-data text-xs text-muted-foreground">{g.progress}%</span></div>
                  <Progress value={g.progress} className="h-2" />
                </div>
              ))}
            </div>
          </Card>

          <Card className="gold-ring glass p-6">
            <div className="mb-3 flex items-center gap-2"><BookText className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Official Documents</h3></div>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-between gold-ring" data-testid="quick-handbook" onClick={() => downloadBlob("/documents/generate/handbook", "Iota_Members_Handbook.pdf")}>
                <span className="flex items-center gap-2"><BookText className="h-4 w-4" /> Members' Handbook</span><FileDown className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full justify-between gold-ring" data-testid="quick-security-brief" onClick={() => downloadBlob("/documents/generate/security-brief", "Iota_Security_Brief.pdf")}>
                <span className="flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Security Brief</span><FileDown className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
